import React, { Component } from 'react'
import { inject, observer } from 'mobx-react';
import { observable } from 'mobx';
import moment from 'moment-timezone';
import Pagination from 'components/Pagination';
import Footer from '../Footer';
import { Alert, Intent } from '@blueprintjs/core';
import Toast from 'helpers/toaster';
import qs from 'qs';
import { formatTimeOnly, formatDateOnly } from 'helpers/time';
import { currencyFormattedAmount } from 'helpers/currency';
import ManualTransactionModal from 'components/Invoice/ManualTransactionModal';
import * as DataList from 'components/DataList';

// Column and card layouts, shared with the page's loading skeleton.
export const INVOICE_COLUMNS = [
  { area: 'invoice', label: 'Invoice #', width: '150px', bone: 'short' },
  { area: 'due', label: 'Due date', width: '128px', bone: 'short', icon: 'calendar' },
  { area: 'job', label: 'Job #', width: '130px', bone: 'short', icon: 'clipboard' },
  { area: 'client', label: 'Customer', width: 'minmax(180px, 1.4fr)', icon: 'map-marker' },
  { area: 'created_by', label: 'Created by', width: 'minmax(150px, 1fr)', icon: 'person' },
  { area: 'total', label: 'Grand total', width: '130px', bone: 'short' },
  { area: 'status', label: 'Payment', width: '128px', bone: 'pill' },
  { area: 'actions', label: '', width: '96px', bone: 'none' },
];

export const INVOICE_CARDS = {
  columns: 'minmax(0, 1fr) auto auto',
  areas: [
    'invoice status actions',
    'client client client',
    'job job job',
    'due due due',
    'created_by total total',
  ],
  footer: ['created_by', 'total'],
};

export const INVOICE_MIN_TABLE_WIDTH = 1200;

const INVOICE_FILTERS = {
  payment_status: 'payment status',
  synced_with_quickbooks: 'QuickBooks sync',
  event_number: 'job number',
  service_type_id: 'service type',
  created_by_id: 'created by',
  client_id: 'customer',
};

const isPaid = (attributes) => attributes.payment_status === 'paid';

const isOverdue = (attributes) =>
  !isPaid(attributes) &&
  attributes.invoice_due_date &&
  moment(attributes.invoice_due_date).isBefore(moment(), 'day');

// Payment status pill: the real payment_status, red when an unpaid invoice
// is past its due date.
const PaymentPill = ({ attributes }) => {
  let tone = 'grey';
  if (attributes.payment_status === 'paid') tone = 'green';
  else if (attributes.payment_status === 'partially_paid') tone = 'amber';
  if (isOverdue(attributes)) tone = 'red';
  return <DataList.Pill tone={tone}>{attributes.formatted_payment_status || 'Unpaid'}</DataList.Pill>;
};

@inject('store', 'invoiceStore', 'viewStore') @observer
class InvoicesTable extends Component {

  @observable isDeleteDialogVisible = false;
  @observable invoice;
  @observable showManualTransactionDialog = false;

  onPaginationChange(newPage) {
    let {
      viewStore: {
        invoiceSearch
      }
    } = this.props;
    this.props.viewStore.invoiceSearch = {
      ...invoiceSearch,
      page: newPage
    };
    this.props.viewStore.invoices();
  }

  // Keep the date range, drop every other filter.
  clearFilters() {
    const { viewStore } = this.props;
    const { where } = viewStore.invoiceSearch;
    viewStore.invoiceSearch = {
      ...viewStore.invoiceSearch,
      where: {
        created_at: where.created_at,
        kind: where.kind,
        event_number: null,
        grand_total: { gte: null, lte: null },
      },
      page: 1,
    };
    viewStore.bumpListFilters();
    viewStore.invoices();
  }

  widenDateRange() {
    const { viewStore } = this.props;
    const { where } = viewStore.invoiceSearch;
    const start = moment.unix(parseInt(where.created_at.gte, 10));
    viewStore.invoiceSearch = {
      ...viewStore.invoiceSearch,
      where: {
        ...where,
        created_at: { ...where.created_at, gte: start.subtract(3, 'months').startOf('day').unix() },
      },
      page: 1,
    };
    viewStore.bumpListFilters();
    viewStore.invoices();
  }

  handleDeleteCancel() {
    this.isDeleteDialogVisible = false;
  }

  handleDeleteConfirm() {
    this.isDeleteDialogVisible = false;
    this.props.invoiceStore.deleteInvoice(this.invoice)
      .then(this.onSuccessfulDeleteInvoice.bind(this))
      .catch(this.onFailedInvoiceDelete.bind(this))
  }

  onSuccessfulDeleteInvoice(response) {
    Toast.show({ icon: 'thumbs-up', message: 'Invoice deleted successfully!', intent: Intent.SUCCESS })
    this.invoice = null;
    this.isDeleteDialogVisible = false;
  }

  onFailedInvoiceDelete(error) {
    this.isDeleteDialogVisible = false;
    Toast.show({ icon: 'warning-sign', message: 'Unable to delete invoice!', intent: Intent.DANGER })
  }

  handleInvoiceClick(event, invoice) {
    switch (event.label) {
      case 'Edit':
        this.props.viewStore.ShowInvoice({ uuid: invoice.attributes.id })
        break;
      case 'Delete':
        this.isDeleteDialogVisible = true;
        this.invoice = invoice;
        break;
      case 'MarkAsPaid':
        this.props.invoiceStore.invoice.object = invoice;
        this.showManualTransactionDialog = true;
        break;
      default:
    }
  }

  onCloseManualTransactionDialog() {
    this.showManualTransactionDialog = false;
  }

  onSuccessManualTransaction() {
    this.showManualTransactionDialog = false;
    this.props.invoiceStore.loadInvoices();
  }

  renderEmpty() {
    const where = this.props.viewStore.invoiceSearch.where || {};
    const filters = DataList.activeFilters(where, INVOICE_FILTERS);
    const total = where.grand_total || {};
    if (total.gte || total.lte) filters.push('grand total');
    return (
      <DataList.Empty
        noun="invoices"
        filters={filters}
        onClearFilters={() => this.clearFilters()}
        extraAction={where.created_at && where.created_at.gte
          ? { label: 'Include 3 more months', icon: 'history', onClick: () => this.widenDateRange() }
          : null}
      />
    );
  }

  render() {
    let {
      viewStore,
      viewStore: {
        invoiceSearch,
        jobTab: {
          section
        }
      },
      invoiceStore: {
        invoices: {
          objects,
          meta
        }
      }
    } = this.props;
    if (objects.length === 0) {
      return this.renderEmpty();
    }
    return (
      <React.Fragment>
        <Footer>
          <div className="bp3-navbar-group responsive-pagination bp3-align-left">
            <Pagination
              value={meta.page}
              total={meta.total_pages}
              onChange={this.onPaginationChange.bind(this)}
            />
          </div>
        </Footer>
        <DataList.List
          columns={INVOICE_COLUMNS}
          cards={INVOICE_CARDS}
          minTableWidth={INVOICE_MIN_TABLE_WIDTH}
          total={meta.total_records || objects.length}
          noun="invoices"
          note={DataList.sortNote(invoiceSearch.order)}
        >
          {objects.map((invoice) => {
            const attributes = invoice.attributes;
            const job = attributes.job || {};
            const client = attributes.client || {};
            const createdBy = attributes.created_by || {};
            const canMarkPaid = !isPaid(attributes) && attributes.due_amount > 0;
            const sent = !isPaid(attributes) && attributes.status === 'sent';
            return (
              <DataList.Row key={invoice.id}>
                <DataList.Cell area="invoice">
                  <div className="dl-lines">
                    <DataList.RowLink href={`/dashboard/invoices/${attributes.id}/show`} label={`Invoice ${attributes.number}`}>
                      #{attributes.number}
                    </DataList.RowLink>
                    <div className="dl-muted dl-truncate">
                      {formatDateOnly(attributes.created_at)}, {formatTimeOnly(attributes.created_at)}
                    </div>
                  </div>
                </DataList.Cell>
                <DataList.Cell area="due">
                  <DataList.Lines
                    primary={attributes.invoice_due_date
                      ? <React.Fragment><DataList.CardOnly>Due </DataList.CardOnly>{formatDateOnly(attributes.invoice_due_date)}</React.Fragment>
                      : <span className="dl-muted">No due date</span>}
                    title={attributes.invoice_due_date ? `Due ${formatDateOnly(attributes.invoice_due_date)}` : undefined}
                    strong={false}
                    secondary={attributes.invoice_due_date
                      ? (isOverdue(attributes) ? 'Overdue' : formatTimeOnly(attributes.invoice_due_date))
                      : null}
                  />
                </DataList.Cell>
                <DataList.Cell area="job">
                  <DataList.Lines
                    primary={<React.Fragment><DataList.CardOnly>Job </DataList.CardOnly>#{attributes.event_number}</React.Fragment>}
                    title={`Job #${attributes.event_number}`}
                    href={job.id ? `/dashboard/jobs/${job.id}/${section}` : undefined}
                    secondary={job.service_type_name}
                  />
                </DataList.Cell>
                <DataList.Cell area="client">
                  <DataList.Lines
                    primary={client.name}
                    href={client.id
                      ? `/dashboard/clients/${client.id}?${qs.stringify(viewStore.clientShowSearch, { encode: false })}`
                      : undefined}
                    secondary={attributes.location && attributes.location.full_address}
                  />
                </DataList.Cell>
                <DataList.Cell area="created_by">
                  <DataList.Lines
                    primary={<React.Fragment><DataList.CardOnly>Created by </DataList.CardOnly>{createdBy.full_name}</React.Fragment>}
                    title={createdBy.full_name}
                    href={createdBy.id ? `/dashboard/users/${createdBy.id}/show?resource=vacations` : undefined}
                    secondary={createdBy.email}
                  />
                </DataList.Cell>
                <DataList.Cell area="total">
                  <DataList.Lines
                    primary={currencyFormattedAmount(attributes.grand_total)}
                    secondary={canMarkPaid ? `${currencyFormattedAmount(attributes.due_amount)} due` : null}
                  />
                </DataList.Cell>
                <DataList.Cell area="status">
                  <div className="dl-lines">
                    <PaymentPill attributes={attributes} />
                    {sent && <div className="dl-muted">{attributes.formatted_status}</div>}
                  </div>
                </DataList.Cell>
                <DataList.Cell area="actions">
                  <DataList.Actions
                    items={[
                      { icon: 'edit', label: 'Edit invoice', onClick: () => this.handleInvoiceClick({ label: 'Edit' }, invoice) },
                      { icon: 'trash', label: 'Delete invoice', intent: 'danger', onClick: () => this.handleInvoiceClick({ label: 'Delete' }, invoice) },
                      canMarkPaid && { icon: 'dollar', label: 'Mark as paid', onClick: () => this.handleInvoiceClick({ label: 'MarkAsPaid' }, invoice) },
                    ]}
                  />
                </DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
        <Alert
          cancelButtonText="Cancel"
          confirmButtonText="Delete"
          icon="trash"
          intent={Intent.DANGER}
          isOpen={this.isDeleteDialogVisible}
          onCancel={this.handleDeleteCancel.bind(this)}
          onConfirm={this.handleDeleteConfirm.bind(this)}
        >
          <p>
            Are you sure you want to do this?
          </p>
        </Alert>
        <ManualTransactionModal
          isOpen={this.showManualTransactionDialog}
          onClose={this.onCloseManualTransactionDialog.bind(this)}
          onSuccess={this.onSuccessManualTransaction.bind(this)}
        />
        <Footer>
          <div className="bp3-navbar-group responsive-pagination bp3-align-left">
            <Pagination
              value={meta.page}
              total={meta.total_pages}
              onChange={this.onPaginationChange.bind(this)}
            />
          </div>
        </Footer>
      </React.Fragment>
    )
  }
}

export default InvoicesTable;
